// Story ▸ Character study (StoryRoom.swift's `CharacterStudyPage`): the person chosen in the column (else the most
// present), as their study page describes them — each bold-labelled answer — beside where they appear in the book (a
// strip of the manuscript's scenes) and who they share scenes with most. Open study page goes to the page in Research &
// Notes; Edit details to Cast & Places. The portrait is the study page's first photo (added on a Mac or iPad; the web
// shows it once the file has reached this browser), else the character's colour.
import { entityCSSColor } from '@writing-suite/core';
import { studyAnswers } from '@writing-suite/core/story';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { followLink } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { opened, storyModel } from '../../store/story.ts';
import { lookUp } from '../../store/lookUp.ts';
import { EntityDot, plural } from './common.tsx';
import { isPicture, useAttachmentURL } from '../Attachments.tsx';

export function StudyPage({ project, params, hrefFor }: PageProps) {
  const ws = useWorkspace();
  const model = storyModel(project);
  const entities = opened(project).record.entities;
  const entity = entities.find((e) => e.id === params.who && e.kind === 'character') ?? model.people[0] ?? null;
  const castHref = hrefFor('plan/cast', null, entity ? { who: entity.id } : {});
  if (!entity) {
    return (
      <div className="story-page study-page">
        <header className="page-head">
          <h1 tabIndex={-1}>Character study</h1>
        </header>
        <div className="story-empty" role="note">
          <p className="story-empty-title">No Characters</p>
          <p>Type @ and a name in Write to bring a character into the story, or add one in Cast &amp; Places.</p>
          <p>
            <a className="quiet-button" href={castHref} onClick={(e) => followLink(e, castHref)}>
              Cast &amp; Places
            </a>
          </p>
        </div>
      </div>
    );
  }
  const seen = model.appearances.get(entity.id) ?? { scenes: 0, pov: 0 };
  const role = entity.role?.trim()
    ? entity.role.trim()[0].toUpperCase() + entity.role.trim().slice(1)
    : seen.pov > 0 ? `Point of view in ${seen.pov} · in ${plural(seen.scenes, 'scene')}` : `In ${plural(seen.scenes, 'scene')}`;
  const page = entity.studyID ? project.fragment(entity.studyID) : null;
  const answers = page ? studyAnswers(page.text) : [];
  const portrait = useAttachmentURL(ws?.live ?? null, page?.attachments.find(isPicture) ?? null);
  const co = model.co;
  const strip = co.strip(entity.id);
  const partners = co.partners(entity.id, model.people.map((p) => p.id)).slice(0, 6);
  const most = partners[0]?.scenes ?? 1;
  return (
    <div className="story-page study-page" data-entity-id={entity.id}>
      <header className="study-head">
        <div className="study-portrait" aria-hidden="true" ref={(el) => el?.style.setProperty('--dot', entityColor(entity.color))}>
          {portrait.url ? <img src={portrait.url} alt="" /> : <EntityDot color={entity.color} size="large" />}
        </div>
        <div>
          <h1 tabIndex={-1} className="study-name">{entity.name}</h1>
          <p className="study-role">{role}</p>
        </div>
      </header>
      <div className="study-layout">
        <div className="study-answers">
          {answers.length ? (
            answers.map((a, i) => (
              <p key={i} className="study-answer">
                <strong>{a.label}.</strong> {a.text}
              </p>
            ))
          ) : (
            <p className="empty-note">
              {entity.studyID ? `Nothing on ${entity.name}’s study page yet. Open it to write about who they are.` : `Turn on study pages in Cast & Places to keep notes on ${entity.name}.`}
            </p>
          )}
        </div>
        <aside className="study-facts" aria-label="Where they appear">
          <p className="eyebrow">Appears in</p>
          <div className={`presence-strip${strip.length > 60 ? ' dense' : ''}`} aria-hidden="true">
            {strip.map((here, i) => (
              <span key={i} className={here ? 'here' : ''} ref={here ? (el) => el?.style.setProperty('--dot', entityColor(entity.color)) : undefined} />
            ))}
          </div>
          <p className="study-count" data-scenes={co.scenes(entity.id)}>
            {co.scenes(entity.id)} of {plural(strip.length, 'scene')} in the manuscript
          </p>
          {partners.length ? (
            <>
              <p className="eyebrow">Shares scenes with</p>
              <ul className="partners">
                {partners.map((p) => {
                  const other = entities.find((e) => e.id === p.entity);
                  if (!other) return null;
                  const href = hrefFor('plan/study', null, { who: other.id });
                  return (
                    <li key={p.entity} aria-label={`${other.name}, ${plural(p.scenes, 'scene')} together`}>
                      <EntityDot color={other.color} />
                      <a href={href} onClick={(e) => followLink(e, href)}>
                        {other.name}
                      </a>
                      <span className="partner-bar" aria-hidden="true" ref={(el) => {
                        el?.style.setProperty('--fraction', String(p.scenes / most));
                        el?.style.setProperty('--dot', entityColor(other.color));
                      }} />
                      <span className="partner-count">{p.scenes}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
        </aside>
      </div>
      <p className="study-actions">
        {entity.studyID ? (
          <button type="button" className="link-button" onClick={() => ws?.reveal({ fragmentId: entity.studyID! })}>
            Open study page →
          </button>
        ) : null}
        <a className="quiet-button" href={castHref} onClick={(e) => followLink(e, castHref)}>
          Edit details
        </a>
        <button type="button" className="link-button" onClick={() => lookUp(project, entity.name)} data-look-up>
          Dossier
        </button>
      </p>
      <p className="field-note study-photo-note">The portrait is the study page’s first photo, which you add on your Mac or iPad.</p>
    </div>
  );
}

const entityColor = (c: string | undefined) => entityCSSColor(c);
